import React, { MouseEventHandler, useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'

import { selectIsProjectSwitchInitiated } from '@store/actionSlices/appConfig'
import { selectProjectConfig } from '@store/actionSlices/buildingPageConfig'
import { setByFlag } from '@store/actionSlices/houseAndLand'

import { HouseAndLandActiveKeyType } from '@api/types/house-and-land-type'

import useProjectSwitchUtils from '@utilities/useProjectSwitchUtils'

import { ProjectType } from '@adUtilities/constants'
import useMapActionRouteUtils from '@adUtilities/map-action-route-utils'
import { notifyError } from '@adUtilities/notifier'
import { DefaultRouteParamKey } from '@adUtilities/types'

import Layer from './layer'

interface LayerHnlProps {
  label: string
  actionRoute: string
}

function LayerHnl({ label, actionRoute }: LayerHnlProps) {
  const dispatch = useDispatch()
  const history = useHistory()

  const { type: currentProjectType } = useSelector(selectProjectConfig)

  const { parseActionRoute } = useMapActionRouteUtils({
    currentProjectType: currentProjectType as ProjectType,
  })

  const { isValid, route, routeParams, fullpath, pathParams, projectType } =
    useMemo(
      () => parseActionRoute(actionRoute),
      [actionRoute, parseActionRoute]
    )

  const { startProjectSwitch } = useProjectSwitchUtils()
  const isRefreshing = useSelector(selectIsProjectSwitchInitiated)
  const [isProjectSwitching, setIsProjectSwitching] = useState(false)

  const hasValidProjectSwitchParam = isValid && !!routeParams?.project

  const handleProjectSwitch = useCallback(async () => {
    if (!isValid) {
      return
    }
    const project = routeParams[DefaultRouteParamKey.Project]

    if (!project) {
      return
    }

    setIsProjectSwitching(true)
    const { status, message } = await startProjectSwitch({
      projectCanonicalName: project,
      redirectTo: String(route),
      redirectUrlParams: pathParams,
    })
    setIsProjectSwitching(false)

    if (!status && message) {
      notifyError(message)
    }
  }, [startProjectSwitch, route, routeParams, pathParams])

  const handleClick = useCallback<MouseEventHandler>(
    async (e) => {
      e.preventDefault()

      if (isRefreshing || isProjectSwitching || !isValid) {
        return
      }

      if (hasValidProjectSwitchParam && routeParams.project) {
        handleProjectSwitch()
        return
      }

      if (projectType !== ProjectType.HouseAndLand) {
        return
      }

      dispatch(
        setByFlag({
          flag: HouseAndLandActiveKeyType.ActivePrecinctId,
          value: routeParams.precinct || '',
        })
      )
      dispatch(
        setByFlag({
          flag: HouseAndLandActiveKeyType.ActiveStageId,
          value: routeParams.stage || '',
        })
      )
      dispatch(
        setByFlag({ flag: HouseAndLandActiveKeyType.ActiveLotId, value: '' })
      )
      dispatch(
        setByFlag({
          flag: HouseAndLandActiveKeyType.ActiveLotName,
          value: routeParams.lot || '',
        })
      )
      if (fullpath) {
        history.push(fullpath)
      }
    },
    [
      handleProjectSwitch,
      projectType,
      isValid,
      routeParams,
      fullpath,
      hasValidProjectSwitchParam,
      isRefreshing,
      isProjectSwitching,
    ]
  )

  return (
    <Layer
      label={label}
      route={(isValid && route) || undefined}
      isValid={isValid}
      onClick={handleClick}
      isProcessing={isProjectSwitching}
      as={hasValidProjectSwitchParam ? 'button' : undefined}
    />
  )
}

export default LayerHnl
